import Cookies from 'js-cookie';

export const COOKIE_KEYS = {
  REFRESH_TOKEN: 'refresh_token',
  ACCESS_TOKEN: 'access_token',
  TOKEN_EXPIRY: 'token_expiry',
} as const;

export const COOKIE_OPTIONS = {
  REFRESH_TOKEN: {
    secure: true,
    sameSite: 'lax' as const,
    expires: 30, // 30 dias
  },
  ACCESS_TOKEN: {
    secure: true,
    sameSite: 'lax' as const,
    expires: 1, // 1 dia
  },
  TOKEN_EXPIRY: {
    secure: true,
    sameSite: 'lax' as const,
    expires: 1, // 1 dia
  },
} as const;

export class CookieManager {
  /**
   * Define o refresh token no cookie
   */
  static setRefreshToken(token: string, options?: Cookies.CookieAttributes): void {
    const cookieOptions = {
      ...COOKIE_OPTIONS.REFRESH_TOKEN,
      ...options,
    };

    Cookies.set(COOKIE_KEYS.REFRESH_TOKEN, token, cookieOptions);

    console.log('[CookieManager] 🍪 Refresh token definido no cookie', {
      tokenLength: token.length,
      options: cookieOptions,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Obtém o refresh token do cookie
   */
  static getRefreshToken(): string | undefined {
    const token = Cookies.get(COOKIE_KEYS.REFRESH_TOKEN);

    console.log('[CookieManager] 🔍 Buscando refresh token:', {
      cookieKey: COOKIE_KEYS.REFRESH_TOKEN,
      hasToken: !!token,
      tokenLength: token?.length || 0,
      tokenStart: token?.substring(0, 20) + '...' || 'N/A',
      allCookies: document.cookie,
      cookiesList: document.cookie.split(';').map(c => c.trim()),
    });

    if (token) {
      console.log('[CookieManager] ✅ Refresh token encontrado no cookie');
    } else {
      console.log('[CookieManager] ❌ Refresh token não encontrado no cookie');
    }
    return token;
  }

  /**
   * Remove o refresh token do cookie
   */
  static removeRefreshToken(): void {
    Cookies.remove(COOKIE_KEYS.REFRESH_TOKEN);
    console.log('[CookieManager] Refresh token removido do cookie');
  }

  /**
   * Define o access token no cookie
   */
  static setAccessToken(token: string, options?: Cookies.CookieAttributes): void {
    const cookieOptions = {
      ...COOKIE_OPTIONS.ACCESS_TOKEN,
      ...options,
    };

    Cookies.set(COOKIE_KEYS.ACCESS_TOKEN, token, cookieOptions);

    console.log('[CookieManager] 🔑 Access token definido no cookie', {
      tokenLength: token.length,
      options: cookieOptions,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Obtém o access token do cookie
   */
  static getAccessToken(): string | undefined {
    const token = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
    if (token) {
      console.log('[CookieManager] Access token encontrado no cookie');
    } else {
      console.log('[CookieManager] Access token não encontrado no cookie');
    }
    return token;
  }

  /**
   * Remove o access token do cookie
   */
  static removeAccessToken(): void {
    Cookies.remove(COOKIE_KEYS.ACCESS_TOKEN);
    console.log('[CookieManager] Access token removido do cookie');
  }

  /**
   * Define a data de expiração do token
   */
  static setTokenExpiry(expiryTimestamp: number, options?: Cookies.CookieAttributes): void {
    Cookies.set(COOKIE_KEYS.TOKEN_EXPIRY, expiryTimestamp.toString(), {
      ...COOKIE_OPTIONS.TOKEN_EXPIRY,
      ...options,
    });
    console.log('[CookieManager] Expiração do token definida:', new Date(expiryTimestamp));
  }

  /**
   * Obtém a data de expiração do token
   */
  static getTokenExpiry(): number | undefined {
    const expiry = Cookies.get(COOKIE_KEYS.TOKEN_EXPIRY);
    if (expiry) {
      const timestamp = parseInt(expiry, 10);
      console.log('[CookieManager] Expiração do token encontrada:', new Date(timestamp));
      return timestamp;
    }
    console.log('[CookieManager] Expiração do token não encontrada');
    return undefined;
  }

  /**
   * Remove a data de expiração do token
   */
  static removeTokenExpiry(): void {
    Cookies.remove(COOKIE_KEYS.TOKEN_EXPIRY);
    console.log('[CookieManager] Expiração do token removida');
  }

  /**
   * Limpa todos os cookies de autenticação
   */
  static clearAllAuthCookies(): void {
    console.log('[CookieManager] 🧹 Iniciando limpeza de todos os cookies de autenticação...');

    this.removeRefreshToken();
    this.removeAccessToken();
    this.removeTokenExpiry();

    // Limpar cookies adicionais que podem existir
    const additionalCookies = [
      'auth_token',
      'refresh_token',
      'token_expiry',
      'user_session',
      'session_id',
      'auth_session',
    ];

    additionalCookies.forEach(cookieName => {
      Cookies.remove(cookieName);
      Cookies.remove(cookieName, { path: '/' });
      Cookies.remove(cookieName, { path: '/', domain: window.location.hostname });
    });

    console.log('[CookieManager] ✅ Todos os cookies de autenticação foram limpos');
  }

  /**
   * Verifica se existe algum token de autenticação
   */
  static hasAnyAuthToken(): boolean {
    const hasAccess = !!this.getAccessToken();
    const hasRefresh = !!this.getRefreshToken();
    return hasAccess || hasRefresh;
  }

  /**
   * Obtém informações sobre todos os cookies de autenticação
   */
  static getAuthCookiesInfo(): {
    hasAccessToken: boolean;
    hasRefreshToken: boolean;
    hasExpiry: boolean;
    expiryDate?: Date;
  } {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    const expiry = this.getTokenExpiry();

    return {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      hasExpiry: !!expiry,
      expiryDate: expiry ? new Date(expiry) : undefined,
    };
  }

  /**
   * Verifica se o refresh token está sendo definido pelo backend
   */
  static checkRefreshTokenFromBackend(): {
    isDefined: boolean;
    tokenValue?: string;
    tokenLength?: number;
    allCookies: string;
    cookieList: string[];
  } {
    const allCookies = document.cookie;
    const cookieList = allCookies.split(';').map(c => c.trim());
    const refreshToken = this.getRefreshToken();

    console.log('[CookieManager] 🔍 Verificação completa do refresh token:', {
      isDefined: !!refreshToken,
      tokenValue: refreshToken,
      tokenLength: refreshToken?.length,
      allCookies,
      cookieList,
      cookieKeys: Object.keys(COOKIE_KEYS),
    });

    return {
      isDefined: !!refreshToken,
      tokenValue: refreshToken,
      tokenLength: refreshToken?.length,
      allCookies,
      cookieList,
    };
  }
}
